import { z } from 'zod'
import { widgetSchema } from '../../common/schema'
import { calendarFilterSchema } from './data'

export const calendarWidgetConfig = widgetSchema.extend({
  section: z.object({
    show: z.boolean().default(false).describe('Show the section title above the widget content'),
    title: z.string().nullish().default('Rates and Availability').describe('Section title text'),
  }).default({ show: false, title: 'Rates and Availability' }).describe('Section heading shown above the widget content'),
  filter: calendarFilterSchema,

  lightModeColors: z.object({
    accent: z.string().default('#f59e0cff').describe('The accent color of the widget'),
    selectableColor: z.string().default('#333333ff').describe('Text color for selectable dates'),
    selectableBackground: z.string().default('#e4f4d988').describe('Background color for selectable dates with alpha'),
    selectablePriceColor: z.string().default('#666666ff').describe('Price color for selectable dates'),

    passthroughColor: z.string().default('#999999ff').describe('Text color for pass-through dates'),
    passthroughBackground: z.string().default('#eeeeeecc').describe('Background color for pass-through dates with alpha'),
    passthroughPriceColor: z.string().default('#999999ff').describe('Price color for pass-through dates'),

    selectedColor: z.string().default('#333333ff').describe('Text color for selected dates'),
    selectedBackground: z.string().default('#b7db9fcc').describe('Background color for selected dates with alpha'),
    selectedPriceColor: z.string().default('#666666ff').describe('Price color for selected dates'),

    blockedColor: z.string().default('#999999ff').describe('Text color for blocked dates'),
    blockedBackground: z.string().default('#eeeeeecc').describe('Background color for blocked dates with alpha'),

    bookedColor: z.string().default('#881616ff').describe('Text color for booked dates'),
    bookedBackground: z.string().default('#fdd2d288').describe('Background color for booked dates with alpha'),

    disabledColor: z.string().default('#999999ff').describe('Text color for disabled dates'),
    disabledBackground: z.string().default('#eeeeeecc').describe('Background color for disabled dates with alpha'),

    unavailableColor: z.string().default('#999999ff').describe('Text color for unavailable dates'),
    unavailableBackground: z.string().default('#eeeeeecc').describe('Background color for unavailable dates with alpha'),
  }).default({
    accent: '#f59e0cff',
    selectableColor: '#333333ff',
    selectableBackground: '#e4f4d988',
    selectablePriceColor: '#666666ff',
    passthroughColor: '#999999ff',
    passthroughBackground: '#eeeeeecc',
    passthroughPriceColor: '#999999ff',
    selectedColor: '#333333ff',
    selectedBackground: '#b7db9fcc',
    selectedPriceColor: '#666666ff',
    blockedColor: '#999999ff',
    blockedBackground: '#eeeeeecc',
    bookedColor: '#881616ff',
    bookedBackground: '#fdd2d288',
    disabledColor: '#999999ff',
    disabledBackground: '#eeeeeecc',
    unavailableColor: '#999999ff',
    unavailableBackground: '#eeeeeecc',
  }).describe('The colors of the widget in light mode'),

  darkModeColors: z.object({
    accent: z.string().default('#f59e0cff').describe('The accent color of the widget'),
    selectableColor: z.string().default('#333333ff').describe('Text color for selectable dates'),
    selectableBackground: z.string().default('#e4f4d988').describe('Background color for selectable dates with alpha'),
    selectablePriceColor: z.string().default('#666666ff').describe('Price color for selectable dates'),

    passthroughColor: z.string().default('#999999ff').describe('Text color for pass-through dates'),
    passthroughBackground: z.string().default('#eeeeeecc').describe('Background color for pass-through dates with alpha'),
    passthroughPriceColor: z.string().default('#999999ff').describe('Price color for pass-through dates'),

    selectedColor: z.string().default('#333333ff').describe('Text color for selected dates'),
    selectedBackground: z.string().default('#b7db9fcc').describe('Background color for selected dates with alpha'),
    selectedPriceColor: z.string().default('#666666ff').describe('Price color for selected dates'),

    blockedColor: z.string().default('#999999ff').describe('Text color for blocked dates'),
    blockedBackground: z.string().default('#eeeeeecc').describe('Background color for blocked dates with alpha'),

    bookedColor: z.string().default('#881616ff').describe('Text color for booked dates'),
    bookedBackground: z.string().default('#fdd2d288').describe('Background color for booked dates with alpha'),

    disabledColor: z.string().default('#999999ff').describe('Text color for disabled dates'),
    disabledBackground: z.string().default('#eeeeeecc').describe('Background color for disabled dates with alpha'),

    unavailableColor: z.string().default('#999999ff').describe('Text color for unavailable dates'),
    unavailableBackground: z.string().default('#eeeeeecc').describe('Background color for unavailable dates with alpha'),
  }).default({
    accent: '#f59e0cff',
    selectableColor: '#333333ff',
    selectableBackground: '#e4f4d988',
    selectablePriceColor: '#666666ff',
    passthroughColor: '#999999ff',
    passthroughBackground: '#eeeeeecc',
    passthroughPriceColor: '#999999ff',
    selectedColor: '#333333ff',
    selectedBackground: '#b7db9fcc',
    selectedPriceColor: '#666666ff',
    blockedColor: '#999999ff',
    blockedBackground: '#eeeeeecc',
    bookedColor: '#881616ff',
    bookedBackground: '#fdd2d288',
    disabledColor: '#999999ff',
    disabledBackground: '#eeeeeecc',
    unavailableColor: '#999999ff',
    unavailableBackground: '#eeeeeecc',
  }).describe('The colors of the widget in dark mode'),

  showPrice: z.boolean().default(true).describe('Show nightly prices on the calendar dates'),
  showBookedPrice: z.boolean().default(false).describe('Show prices on dates that are already booked'),
  maxStay: z.number().default(40).describe('Maximum number of nights that can be booked'),
  bookingWindow: z.number().default(0).describe('Number of days in advance bookings can be made'),
  maxCalendarDays: z.number().int().min(1).default(365).describe('How far forward the calendar renders and accepts date selections, in days (capped by the booking window)'),
  firstDayOfWeek: z.number().int().min(0).max(6).default(0).describe('Weekday the calendar week starts on'),
  currency: z.string().default('USD').describe('Currency code for displaying prices'),
  showClearButton: z.boolean().default(true).describe('Show the Clear Dates button below the calendar'),
  startMonth: z.object({
    year: z.number().int().nullish(),
    month: z.number().int().min(1).max(12).nullish(),
  }).default({
    year: null,
    month: null,
  }).describe('The initial month to display in the calendar'),

  allowSelectDates: z.boolean().default(true).describe('Let visitors select a date range; when off the calendar is read-only'),
  monthsPerView: z.enum(['auto', 'single', 'double']).default('auto').describe('Months shown at once: auto (responsive — two on desktop, one on mobile), single, or double'),
  displayMode: z.enum(['inline', 'modal']).default('inline').describe('Render the calendar inline, or inside a modal opened by clicking elements matching the trigger selector'),
  modalTriggerSelector: z.string().nullish().describe('CSS selector for the page elements that open the calendar modal (e.g. .open-calendar)'),
  showHeaderOffer: z.boolean().default(true).describe('Show weekly/monthly discount offer text in the calendar header'),
  showLocalTime: z.boolean().default(false).describe("Show the property's local time in the calendar header"),
  showFooterInfo: z.boolean().default(true).describe('Show the footer with the color legend below the calendar'),

  translations: z.object({
    error_disabled_error: z.record(z.string(), z.string()).default({
      en_US: 'Sorry, this date is unavailable',
    }).describe('The error message for disabled dates translation for different languages'),
    error_check_in_min_stay_error: z.record(z.string(), z.string()).default({
      en_US: "Can't check out, this home requires a {{value}} minimum",
    }).describe('The error message for minimum stay requirement translation for different languages'),
    error_select_range_between_unavailable_days_error: z.record(z.string(), z.string()).default({
      en_US: 'Please select a range between available days',
    }).describe('The error message for invalid date range translation for different languages'),
    error_advance_notice_error: z.record(z.string(), z.string()).default({
      en_US: 'Requires {{days}}-day advance booking',
    }).describe('The error message for dates inside the advance-notice window translation for different languages'),
    tip_select_stay: z.record(z.string(), z.string()).default({
      en_US: 'Select {{from}} – {{to}} · {{nights}}',
    }).describe('The tooltip button label suggesting the nearest valid stay translation for different languages'),
    tip_night_one: z.record(z.string(), z.string()).default({
      en_US: '{{n}} night',
    }).describe('The singular nights label for the tooltip suggestion translation for different languages'),
    tip_night_other: z.record(z.string(), z.string()).default({
      en_US: '{{n}} nights',
    }).describe('The plural nights label for the tooltip suggestion translation for different languages'),
    header_offer_weekly_and_monthly: z.record(z.string(), z.string()).default({
      en_US: 'Save {{weekly}}% on weekly and {{monthly}}% on monthly stays!',
    }).describe('Header discount text when both weekly and monthly discounts apply'),
    header_offer_weekly: z.record(z.string(), z.string()).default({
      en_US: 'Save {{weekly}}% on weekly stays!',
    }).describe('Header discount text when only weekly discount applies'),
    header_offer_monthly: z.record(z.string(), z.string()).default({
      en_US: 'Save {{monthly}}% on monthly stays!',
    }).describe('Header discount text when only monthly discount applies'),
    header_local_time: z.record(z.string(), z.string()).default({
      en_US: 'Local time: ',
    }).describe('Label for the local time display'),
    footer_taxes_note: z.record(z.string(), z.string()).default({
      en_US: 'Taxes and fees are additional',
    }).describe('Footer note about taxes and fees'),
    footer_legend_available: z.record(z.string(), z.string()).default({
      en_US: 'Available for Check In/Check Out',
    }).describe('Footer legend label for available dates'),
    footer_legend_booked: z.record(z.string(), z.string()).default({
      en_US: 'Booked',
    }).describe('Footer legend label for booked dates'),
    footer_legend_unavailable: z.record(z.string(), z.string()).default({
      en_US: 'Unavailable or Restricted Dates Such As Minimum Stays',
    }).describe('Footer legend label for unavailable/restricted dates'),
    footer_clear_dates: z.record(z.string(), z.string()).default({
      en_US: 'Clear Dates',
    }).describe('Footer clear-dates button label'),
    // #1262: the unit word the minimum-stay error substitutes into {{value}}
    // ("a 3 night minimum"). English uses the singular for any count; a locale
    // table can pluralize.
    min_stay_unit_one: z.record(z.string(), z.string()).default({
      en_US: 'night',
    }).describe('Unit word inside the minimum-stay error ({{value}} = count + this word) when the count is 1'),
    min_stay_unit_other: z.record(z.string(), z.string()).default({
      en_US: 'night',
    }).describe('Unit word inside the minimum-stay error ({{value}} = count + this word) when the count is more than 1'),
  }).default({
    error_disabled_error: {
      en_US: 'Sorry, this date is unavailable',
    },
    error_check_in_min_stay_error: {
      en_US: "Can't check out, this home requires a {{value}} minimum",
    },
    error_select_range_between_unavailable_days_error: {
      en_US: 'Please select a range between available days',
    },
    error_advance_notice_error: {
      en_US: 'Requires {{days}}-day advance booking',
    },
    tip_select_stay: {
      en_US: 'Select {{from}} – {{to}} · {{nights}}',
    },
    tip_night_one: {
      en_US: '{{n}} night',
    },
    tip_night_other: {
      en_US: '{{n}} nights',
    },
    header_offer_weekly_and_monthly: {
      en_US: 'Save {{weekly}}% on weekly and {{monthly}}% on monthly stays!',
    },
    header_offer_weekly: {
      en_US: 'Save {{weekly}}% on weekly stays!',
    },
    header_offer_monthly: {
      en_US: 'Save {{monthly}}% on monthly stays!',
    },
    header_local_time: {
      en_US: 'Local time: ',
    },
    footer_taxes_note: {
      en_US: 'Taxes and fees are additional',
    },
    footer_legend_available: {
      en_US: 'Available for Check In/Check Out',
    },
    footer_legend_booked: {
      en_US: 'Booked',
    },
    footer_legend_unavailable: {
      en_US: 'Unavailable or Restricted Dates Such As Minimum Stays',
    },
    footer_clear_dates: {
      en_US: 'Clear Dates',
    },
    min_stay_unit_one: {
      en_US: 'night',
    },
    min_stay_unit_other: {
      en_US: 'night',
    },
  }).describe('The translations of the widget'),
});

export type CalendarWidgetConfig = z.infer<typeof calendarWidgetConfig>
